/**
 * organIsolate3D.js — ТОЧНЫЙ ПОКАЗ ОРГАНОВ В 3D ПО МАСКЕ. [12.0.18]
 *
 * ЗАЧЕМ. Кнопки «Селезёнка», «Поджелуд.», «Почки паренх.» работают через
 * пресет — фильтр по плотности. У этого способа есть потолок, и он не в
 * подборе точек кривой, а в самих числах:
 *
 *     натив:     селезёнка 40–55, поджелудочная 30–50, печень 45–65, мышца 40–55
 *     портальная: поджелудочная 80–110, печень 90–110
 *
 * Окна перекрываются полностью. Функция «плотность → прозрачность» не знает,
 * ГДЕ воксель, поэтому вместе с селезёнкой она обязана показать край печени и
 * поясничную мышцу той же плотности. Это предел метода, а не недоделка.
 *
 * Маска знает форму, и плотность ей безразлична. Сеть organsSeg (модель
 * total_6mm.onnx, 117 структур) размечает ровно нужные органы: spleen,
 * pancreas, kidney_left, kidney_right. Этот модуль связывает разметку с
 * объёмным окном: запускает сеть, если маски ещё нет, раскладывает органы по
 * сегментам и показывает их через organView, каждый своим цветом.
 *
 * ЧЕМ ПЛАТИМ, ЧЕСТНО. Шаг модели 6 мм грубее среза исследования: край органа
 * будет ступенчатее, чем при ручной обводке. Для формы и объёма крупных
 * паренхиматозных органов этого обычно довольно, для тонких структур — нет.
 * Первый прогон занимает несколько секунд (у владельца на томе 512×512×808 —
 * около полутора секунд на три заплаты), дальше маска переиспользуется.
 *
 * ЦВЕТА [12.0.36] — природные. Прежние (с 12.0.18) подбирались ради
 * различимости: селезёнка малиновая, поджелудочная охра, почки оранжевые —
 * владелец: «цвет крайне далёк от естественного». Теперь цвет живой ткани, а
 * различимость держат лёгкие сдвиги оттенка и светлоты: селезёнка уходит в
 * лиловый, почки светлее и краснее печени, поджелудочная бледная. Поверх
 * цвета каждая вершина светлеет или темнеет по плотности снимка под ней
 * (organSurfaceCore.цветаПоПлотности).
 *
 * EXPORTS: НАБОРЫ, наборДляПресета, показатьОрганы, переключитьОрган,
 *          видимыеОрганы, вернутьТелоИзОрганов, органыПоказаны
 */

import { log } from '../utils/logger.js';
// [12.0.28] Орган → сегмент — из общего реестра: туда пишут и панель «Органы
// автоматом» (6 / 3 / 1,5 мм), и импорт масок TotalSegmentator.
import { записьОргана, органыМаски, готоваяГодится, модельДляОргана, свободныйСегмент, шагМодели, забытьОрганы, модельКнопок, составПрогона, ПЕЧЁНОЧНАЯ } from './organRegistry.js';
import { структурыМодели, МОДЕЛИ } from '../ai/organsMath.js';

/** Цвета органов. Одни и те же и в 3D, и в подписях. */
export const ЦВЕТА = Object.freeze({
  spleen: '#66273f',         // [12.0.36] тёмный лилово-багровый — как селезёнка вживую
  pancreas: '#d6ad86',       // [12.0.36] бледный розовато-кремовый, дольчатая железа
  kidney_left: '#8f3b2c',    // [12.0.36] красно-коричневый, светлее и краснее печени
  kidney_right: '#8f3b2c',
  // [12.0.26] Печень и доли лёгких — те же 117 структур модели total_6mm.
  liver: '#6e2c1f',          // [12.0.36] тёмный красно-коричневый — самый тёмный из брюшных
  // Доли — оттенками одного розового: верхние светлее, нижние темнее, чтобы
  // междолевые щели читались без подписей.
  lung_upper_lobe_left: '#d49aa2',
  lung_lower_lobe_left: '#a8666f',
  lung_upper_lobe_right: '#d49aa2',
  lung_middle_lobe_right: '#bd8089',
  lung_lower_lobe_right: '#a8666f',
});

/**
 * Наборы органов, привязанные к кнопкам панели.
 *
 * Ключи совпадают с data-render-preset соответствующих кнопок: включён режим
 * «по маске» — кнопка ведёт сюда, выключен — в обычный пресет. Подписи кнопок
 * при этом не меняются, как и договаривались.
 */
export const НАБОРЫ = Object.freeze({
  'custom-17-ct-spleen': Object.freeze({
    подпись: 'Селезёнка',
    органы: Object.freeze(['spleen']),
  }),
  'custom-16-ct-pancreas': Object.freeze({
    подпись: 'Поджелудочная',
    органы: Object.freeze(['pancreas']),
  }),
  'custom-18-ct-kidney-native': Object.freeze({
    подпись: 'Почки',
    органы: Object.freeze(['kidney_left', 'kidney_right']),
  }),
  'custom-02-ct-kidneys': Object.freeze({
    подпись: 'Почки',
    органы: Object.freeze(['kidney_left', 'kidney_right']),
  }),
  'custom-20-ct-kidneys-spleen': Object.freeze({
    подпись: 'Почки и селезёнка',
    органы: Object.freeze(['kidney_left', 'kidney_right', 'spleen']),
  }),
  // [12.0.32] Печень добавлена. Владелец: «органы брюшной полости — появились
  // без печени. Разве печень не орган брюшной полости?» Кнопка называлась
  // «Органы живота», а набор был «поджелудочная, селезёнка, почки».
  // [12.0.35] Подпись — «Органы брюшной полости», как в анатомии. Владелец:
  // «органы живота» — написано безграмотно. Ключ кнопки не менялся.
  'custom-21-ct-upper-abdomen-organs': Object.freeze({
    подпись: 'Органы брюшной полости',
    органы: Object.freeze(['liver', 'pancreas', 'spleen', 'kidney_left', 'kidney_right']),
  }),
  // [12.0.26] Печень: на артериальной фазе (60–80 HU) и нативе (45–65) она
  // вровень с мышцей, порогом не отделить. Туда кнопка уходит сама — правило
  // в core/presetRouteCore.js; на портальной фазе — по плотности, если
  // тумблер «Точно (по маске)» выключен.
  // [12.0.33] «Почки экскр.»: на неэкскреторной серии пресет по плотности
  // показывает не то (кость и сосуды той же плотности — «мусор» на скрине
  // владельца). Органная кнопка — значит, по маске, как «Почки паренх.».
  'custom-19-ct-kidney-excretory': Object.freeze({
    подпись: 'Почки',
    органы: Object.freeze(['kidney_left', 'kidney_right']),
  }),
  'custom-01-ct-liver': Object.freeze({
    подпись: 'Печень',
    органы: Object.freeze(['liver']),
  }),
  // [12.0.26] Лёгкие по долям. Порогом чисто не выходит: частичный объём на
  // границе воздух/кожа и газ в кишке проходят через ту же плотность, что
  // паренхима. Ключ — data-render-preset кнопки «Лёгкие 3D».
  'ct-lung-3d': Object.freeze({
    подпись: 'Лёгкие',
    органы: Object.freeze(['lung_upper_lobe_left', 'lung_lower_lobe_left',
      'lung_upper_lobe_right', 'lung_middle_lobe_right', 'lung_lower_lobe_right']),
  }),
});

/** Набор органов для кнопки, если он для неё заведён. */
export function наборДляПресета(ключ) {
  return НАБОРЫ[String(ключ)] || null;
}

/** Что сейчас показано в объёмном окне. */
let _показано = [];

/** Показаны ли органы вместо тела. */
export function органыПоказаны() { return _показано.length > 0; }

/** Имена органов, видимых сейчас. */
export function видимыеОрганы() { return _показано.slice(); }

/**
 * [12.0.28] Своя карта «орган → сегмент» (_сегментОргана) УБРАНА. Она
 * заполнялась только прогонами этих кнопок, поэтому готовую разметку врача —
 * 3 мм, 1,5 мм, TotalSegmentator — кнопки не видели и каждый раз гнали грубую
 * 6-мм модель («кубики» на скринах владельца). И она не сбрасывалась никогда:
 * забытьРазметку никто не звал. Теперь — общий реестр core/organRegistry.js,
 * где каждая запись привязана к сегментации.
 */

/** Активная сегментация и её маска — если уже есть. */
async function _маска() {
  try {
    const [{ getActiveSegmentationId }, { томМаски }] = await Promise.all([
      import('../tools/toolManager.js'), import('./organView.js'),
    ]);
    const segId = getActiveSegmentationId?.() ?? null;
    const м = segId ? томМаски() : { ошибка: 'сегментации нет' };
    return { segId, пиксели: м.ошибка ? null : м.пиксели };
  } catch (e) {
    log.debug('[Органы-3D] маска:', e.message);
    return { segId: null, пиксели: null };
  }
}

/** Есть ли в сегменте хоть один воксель. */
async function _естьВоксели(пиксели, сегмент) {
  if (!пиксели) return false;
  const { подсчётВокселей } = await import('./organMath.js');
  return подсчётВокселей(пиксели, сегмент) > 0;
}

/** Знает ли модель структуру — по её собственной раскладке. */
const _знает = (модель, орган) => !!МОДЕЛИ[модель] && структурыМодели(модель).includes(орган);

/** Подпись модели для врача. */
export const подписьМодели = (модель) => (модель === ПЕЧЁНОЧНАЯ ? 'отдельная модель печени'
  : МОДЕЛИ[модель]?.подпись || String(модель || ''));

/**
 * Показать набор органов в объёмном окне.
 *
 * [12.0.28] ПОРЯДОК:
 *   1. Орган уже размечен в этой сегментации (панелью, TotalSegmentator,
 *      импортом .nii) и разметка не грубее модели из панели — берём её.
 *   2. Иначе считаем моделью, выбранной в панели «Органы автоматом»
 *      (о.модель); не знает орган — ближайшей, что знает (модельДляОргана).
 *   3. Новая разметка кладётся в сегменты, которых в маске ещё НЕТ.
 *
 * @param {string} ключ — идентификатор кнопки (см. НАБОРЫ)
 * @param {object} [о]
 * @param {(доля:number, текст:string)=>void} [о.ход] — отчёт о ходе разметки
 * @param {string} [о.модель] — модель из панели «Органы автоматом»
 * @param {(план:{готовые:Array, считать:Array})=>void} [о.план] — что будет
 *   сделано, до начала счёта: чтобы врач знал, ждать ли минуты
 * @returns {Promise<{ок:boolean, причина?:string, мл?:number, органы?:Array, считали?:boolean, окно?:string, готовые?:Array, посчитано?:Array}>}
 */
export async function показатьОрганы(ключ, о = {}) {
  const набор = наборДляПресета(ключ);
  if (!набор) return { ок: false, причина: `для «${ключ}» набор органов не заведён` };

  const сообщить = typeof о.ход === 'function' ? о.ход : () => {};
  const нужны = набор.органы.slice();
  // [12.0.29] Модель кнопок: 1,5 мм — если выбрана в панели, иначе 3 мм
  // (даже при «грубой 6 мм» по умолчанию). Готовая разметка грубее — не годится.
  const модель = модельКнопок(о.модель);
  const шагНужный = шагМодели(модель);

  // 1. Что уже размечено и годится
  const { segId, пиксели } = await _маска();
  const готовые = [];
  const нехватает = [];
  for (const имя of нужны) {
    const з = записьОргана(имя, segId);
    const есть = з ? await _естьВоксели(пиксели, з.сегмент) : false;
    if (готоваяГодится(з, { есть, шагНужный })) готовые.push({ орган: имя, источник: з.источник, модель: з.модель, шагМм: з.шагМм });
    else нехватает.push(имя);
  }

  // 2. [12.0.29] Печень — сперва отдельной моделью liver_unet: каждый срез в
  //    полном разрешении, около минуты. Не вышло (нет файла, сеть не нашла) —
  //    печень уходит в общий прогон ниже, и врачу говорится почему.
  const печеньОтдельно = нехватает.includes('liver');
  try {
    о.план?.({
      готовые,
      печень: печеньОтдельно ? ПЕЧЁНОЧНАЯ : null,
      считать: [],
      модель,
    });
  } catch (e) { /* план — только для подписи */ }
  let считали = false;
  let печеньЗамена = null;
  if (печеньОтдельно) {
    сообщить(0.01, 'печень — отдельной моделью');
    try {
      const { печеньАвтоматом } = await import('../ai/liverSeg.js');
      const { пиксели: сейчас } = await _маска();
      // liverSeg сам записывает печень в реестр — второго пути нет.
      const рп = await печеньАвтоматом({ сегмент: свободныйСегмент(сейчас), ход: сообщить });
      if (рп.ок) {
        считали = true;
        нехватает.splice(нехватает.indexOf('liver'), 1);
      } else {
        печеньЗамена = рп.причина || 'модель печени не сработала';
        log.warn(`[Органы-3D] отдельная модель печени: ${печеньЗамена} — считаю печень общей моделью`);
      }
    } catch (e) {
      печеньЗамена = e.message;
      log.warn('[Органы-3D] отдельная модель печени сорвалась:', e.message);
    }
  }

  // 3. Остальное — ОДНИМ прогоном вместе с брюшными органами, которых ещё
  //    нет: время прогона почти не зависит от числа органов, а все органные
  //    кнопки в этом исследовании станут мгновенными.
  const { segId: segIdСейчас } = await _маска();
  const готовБрюшной = (орган) => {
    const з = записьОргана(орган, segIdСейчас);
    return !!з && готоваяГодится(з, { есть: true, шагНужный });
  };
  const поМоделям = new Map();
  const незнаемые = [];
  // [12.0.30] Прогон — только если кнопке правда чего-то не хватает. Прежде
  // «Печень», уже посчитанная liver_unet, всё равно запускала прогон 3 мм ради
  // брюшных органов, которых никто не просил, — владелец увидел их в кадре.
  const состав = нехватает.length ? составПрогона({ нехватает, готов: готовБрюшной, модель, знает: _знает }) : [];
  for (const имя of состав) {
    const { модель: м, замена } = модельДляОргана(имя, модель, _знает);
    if (!м) { незнаемые.push(имя); continue; }
    if (!поМоделям.has(м)) поМоделям.set(м, { модель: м, замена, органы: [] });
    поМоделям.get(м).органы.push(имя);
  }
  const считать = [...поМоделям.values()];
  if (считать.length) {
    try { о.план?.({ готовые: [], печень: null, печеньЗамена, считать, незнаемые, модель }); } catch (e) { /* подпись */ }
  }
  for (const г of считать) {
    сообщить(0.01, `размечаю: ${набор.подпись.toLowerCase()} — ${подписьМодели(г.модель)}`);
    try {
      const { органыАвтоматом } = await import('../ai/organsSeg.js');
      const { пиксели: сейчас } = await _маска();
      const р = await органыАвтоматом({
        органы: г.органы,
        // Новые сегменты — за наибольшим занятым в МАСКЕ, а не в своей карте:
        // иначе кнопка писала бы поверх сегментов панели и TotalSegmentator.
        первыйСегмент: свободныйСегмент(сейчас),
        модель: г.модель,
        ход: сообщить,
      });
      // В реестр разметка записывается самой organsSeg — второго пути нет.
      if (!р.ок) return { ок: false, причина: р.причина };
      считали = true;
    } catch (e) {
      log.warn('[Органы-3D] разметка сорвалась:', e.message);
      return { ок: false, причина: 'разметка не запустилась: ' + e.message };
    }
  }

  // 3. Показ — по реестру, уже с новыми записями
  const { segId: segIdПосле } = await _маска();
  const сегменты = [];
  for (const имя of нужны) {
    const з = записьОргана(имя, segIdПосле);
    // запись несёт шаг разметки — по нему подбирается сглаживание поверхности
    if (з) сегменты.push({ индекс: з.сегмент, цвет: ЦВЕТА[имя] || '#ef4444', орган: имя, запись: з });
  }
  if (!сегменты.length) {
    return { ок: false, причина: 'сеть не нашла эти органы на данном исследовании' };
  }

  try {
    // [12.0.30] Поверхностями ПОВЕРХ снимка, а не подменой тома маской:
    // тело остаётся на месте, чужие органы не подмешиваются цветом.
    const { показатьПоверхности } = await import('./organSurface3D.js');
    const { applyRenderPreset } = await import('./viewport.js');
    // [12.0.33] По умолчанию новая кнопка ЗАМЕНЯЕТ показанные органы; копить —
    // тумблером «Добавлять органы» (о.копить). В 12.0.32 копили всегда, и
    // «Селезёнка» после «Почки+селез.» ничего не меняла — владелец: «ничего не
    // изменилось, осталось изображение почек и селезёнки от предыдущего».
    const копить = !!о.копить;
    const р = await показатьПоверхности({ сегменты, ход: сообщить, копить });
    if (!р.ок) return р;
    // Фон — тело: скелет и призрак кожи, чтобы было видно, где орган лежит.
    try { applyRenderPreset('ct-organ-context', { ww: 1400, wc: 300 }); } catch (e) { log.debug('[Органы-3D] фон:', e.message); }
    _показано = копить ? [...new Set([..._показано, ...сегменты.map((с) => с.орган)])] : сегменты.map((с) => с.орган);
    // [12.0.33] Сопоставление ПО ИМЕНИ органа, а не по порядку: показ
    // пропускает органы, у которых поверхность не построилась, и по номеру
    // объёмы соседей сдвигались на одного (в журнале 12.0.30 так и было:
    // «spleen 0.0 мл, kidney_left 0.0 мл» при живых сегментах).
    const поОрганам = сегменты.map((с) => {
      const из = (р.органы || []).find((о) => о.орган === с.орган && о.индекс === с.индекс);
      return {
        орган: с.орган,
        сегмент: с.индекс,
        цвет: с.цвет,
        мл: из?.мл ?? 0,
        размах: из?.размах ?? null,   // мм, по главным осям
        показан: !!из,
      };
    });
    log.info(`[Органы-3D] показаны в окне ${р.окно}: ${поОрганам.map((о2) => `${о2.орган} ${о2.мл.toFixed(1)} мл`).join(', ')}`
      + ` · готовых ${готовые.length}, посчитано ${считать.map((г) => `${г.органы.join('+')} (${г.модель})`).join(', ') || '—'}`);
    return { ок: true, мл: р.мл, органы: поОрганам, считали, окно: р.окно, готовые, посчитано: считать, печеньЗамена };
  } catch (e) {
    log.warn('[Органы-3D] показ:', e.message);
    return { ок: false, причина: 'не удалось показать органы: ' + e.message };
  }
}

/**
 * [12.0.39] ПОКАЗАТЬ В 3D ВСЮ РАЗМЕТКУ — кнопка панели «Органы автоматом».
 *
 * Владелец: «сегментация не рисует органы в 3Д, а пресет рисует». Здесь —
 * то же, что делает кнопка органов после счёта (поверхности + фон), но для
 * ВСЕГО, что лежит в маске: размеченное моделью берёт имя и шаг из реестра,
 * нарисованное кистью — «сегмент-N». Моделей не запускает: только показ.
 * Кого и каким цветом — чистое ядро segments3DCore.js.
 *
 * @param {{ход?:(доля:number, текст:string)=>void, копить?:boolean}} [о]
 */
export async function показатьРазметкуВ3D(о = {}) {
  const сообщить = typeof о.ход === 'function' ? о.ход : () => {};
  const { segId, пиксели } = await _маска();
  if (!пиксели) return { ок: false, причина: 'разметки нет — сначала «Разметить органы» или нарисуйте кистью' };
  const { сегментыВМаске, составПоказа } = await import('./segments3DCore.js');
  сообщить(0.02, 'смотрю, что размечено');
  const сегменты = составПоказа(органыМаски(segId), сегментыВМаске(пиксели), ЦВЕТА);
  if (!сегменты.length) return { ок: false, причина: 'в маске нет размеченных структур — сначала «Разметить органы»' };
  try {
    const { показатьПоверхности } = await import('./organSurface3D.js');
    const { applyRenderPreset } = await import('./viewport.js');
    const р = await показатьПоверхности({ сегменты, ход: сообщить, копить: !!о.копить });
    if (!р.ок) return р;
    // Фон — тот же, что у кнопки органов: тело в палитре «Обзорного».
    try { applyRenderPreset('ct-organ-context', { ww: 1400, wc: 300 }); } catch (e) { log.debug('[Разметка-3D] фон:', e.message); }
    _показано = сегменты.map((с) => с.орган);
    const органы = сегменты.map((с) => {
      const из = (р.органы || []).find((о2) => о2.орган === с.орган && о2.индекс === с.индекс);
      return { орган: с.орган, сегмент: с.индекс, безИмени: с.безИмени, мл: из?.мл ?? 0, показан: !!из };
    });
    log.info(`[Разметка-3D] в окне ${р.окно}: показано ${органы.filter((о2) => о2.показан).length} из ${органы.length}`
      + ` (по реестру ${сегменты.filter((с) => !с.безИмени).length}, без имени ${сегменты.filter((с) => с.безИмени).length})`);
    return { ок: true, окно: р.окно, органы };
  } catch (e) {
    log.warn('[Разметка-3D] показ:', e.message);
    return { ок: false, причина: 'не удалось показать разметку: ' + e.message };
  }
}

/**
 * Включить или выключить один орган, не трогая остальные.
 *
 * Пресетом так нельзя в принципе: у него один порог на всю картинку. По маске
 * это просто список сегментов в ступеньке непрозрачности.
 *
 * @param {string} орган — имя как в organsMath.КЛАССЫ (spleen, pancreas, …)
 * @param {boolean} видим
 */
export async function переключитьОрган(орган, видим) {
  const имя = String(орган);
  const было = _показано.slice();
  const стало = видим
    ? (было.includes(имя) ? было : [...было, имя])
    : было.filter((и) => и !== имя);

  if (!стало.length) {
    return { ок: false, причина: 'нельзя выключить последний орган — верните тело' };
  }

  const { segId } = await _маска();   // [12.0.28] из общего реестра
  const сегменты = [];
  for (const и of стало) {
    const з = записьОргана(и, segId);
    if (з) сегменты.push({ индекс: з.сегмент, цвет: ЦВЕТА[и] || '#ef4444', орган: и, запись: з });
  }
  if (!сегменты.length) return { ок: false, причина: 'эти органы ещё не размечены' };

  const { показатьПоверхности } = await import('./organSurface3D.js');   // [12.0.30]
  const р = await показатьПоверхности({ сегменты });
  if (!р.ок) return р;
  _показано = сегменты.map((с) => с.орган);
  return { ок: true, мл: р.мл, органы: _показано.slice() };
}

/**
 * Вернуть в окно тело. Пресет объёмной отрисовки пере-применяет вызывающий —
 * у него есть память о последнем выбранном (sidebarBindings).
 */
export async function вернутьТелоИзОрганов() {
  try {
    // [12.0.30] Снять поверхности органов; подмена тома маской осталась
    // только у старого пути (organView.изолироватьОрган от кисти) — если
    // тело всё же спрятано им, возвращаем и его.
    const { убратьПоверхности } = await import('./organSurface3D.js');
    const { вернутьТело, телоСпрятано, окноДляОрганов } = await import('./organView.js');
    const снято = убратьПоверхности(окноДляОрганов());
    _показано = [];
    if (!телоСпрятано()) return { ок: true, нечегоВозвращать: снято === 0, поверхностей: снято };
    const р = await вернутьТело();
    return р.ок ? { ...р, поверхностей: снято } : р;
  } catch (e) {
    return { ок: false, причина: e.message };
  }
}

/**
 * Забыть разметку — например, когда врач открыл другое исследование.
 * Номера сегментов прежнего тома к новому отношения не имеют.
 */
export function забытьРазметку() {
  забытьОрганы();   // [12.0.28] общий реестр; записи и так привязаны к сегментации
  _показано = [];
}

export default {
  НАБОРЫ, ЦВЕТА, наборДляПресета, показатьОрганы, переключитьОрган,
  видимыеОрганы, органыПоказаны, вернутьТелоИзОрганов, забытьРазметку,
};
